import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import useContact from "src/hooks/useContact";
import useReference from "src/hooks/useReference";
import useReport from "src/hooks/useReport";
import { Campaign } from "src/models/Campaign";
import { CsrContact } from "src/models/CsrContact";
import { MerchantGroup } from "src/models/MerchantGroup";
import { MerchantGroupLocation } from "src/models/MerchantGroupLocation";
import { Program } from "src/models/Program";
import { ReportGroup } from "src/models/ReportGroup";
import { TimeZoneType, TimeZoneTypes } from "src/models/enum/TimeZoneTypes";
import { ContactApi } from "src/services/api/ContactApi";

interface SearchCriteriaProps {
  usesLocations: boolean;
  usesUsers: boolean;
  usesCampaigns: boolean;
  handleViewReport: () => void;
  handleDownloadReport: () => void;
}

const SearchCriteria = ({
  usesLocations,
  usesUsers,
  usesCampaigns,
  handleViewReport,
  handleDownloadReport,
}: SearchCriteriaProps) => {
  const { programs } = useContact();
  const { exceedsMaxDateRange, reportInputs, setReportInputs } = useReport();
  const [merchantGroups, setMerchantGroups] = useState<MerchantGroup[]>([]);
  const [reportGroups, setReportGroups] = useState<ReportGroup[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [allMerchantGroupsSelected, setAllMerchantGroupsSelected] =
    useState<boolean>(true);
  const [locations, setLocations] = useState<MerchantGroupLocation[]>([]);
  const [csrContacts, setCsrContacts] = useState<CsrContact[]>([]);
  const { appSettings } = useReference();

  useEffect(() => {
    if ((reportInputs?.programId ?? 0) > 0) {
      getMerchantGroups(reportInputs.programId!);
      getReportGroups(reportInputs.programId!);
      setAllMerchantGroupsSelected(true);
    }
  }, []);

  const handleDateChange = (id: string, date: Date | null) => {
    setReportInputs({
      ...reportInputs,
      [id]: date,
    });
  };

  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.currentTarget;
    setReportInputs({
      ...reportInputs,
      [id]: value,
      locations: [],
      csr: null,
      reportGroupId: "",
      merchantGroups: [""],
    });
    if (value) {
      getMerchantGroups(Number(value));
      getReportGroups(Number(value));
      if (usesCampaigns) getCampaigns(Number(value));
    }
    setAllMerchantGroupsSelected(true);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.currentTarget;
    setReportInputs({
      ...reportInputs,
      [id]: value,
    });
  };

  const handleMerchantGroupChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedValues: string[] = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    const { id } = e.currentTarget;
    const hasAllSelected =
      (selectedValues?.length ?? 0) === 1 && selectedValues?.includes("");

    const hasMultipleMerchantGroups = (selectedValues?.length ?? 0) > 1;

    const csr = reportInputs.csr;

    setReportInputs({
      ...reportInputs,
      [id]: selectedValues,
      locations:
        hasAllSelected || hasMultipleMerchantGroups
          ? [""]
          : [...reportInputs.locations],
      csr: hasAllSelected || hasMultipleMerchantGroups ? null : csr,
    });
    if (
      selectedValues &&
      selectedValues.length === 1 &&
      selectedValues[0] !== ""
    ) {
      getLocations(selectedValues);
    }

    if (!hasAllSelected && (selectedValues?.length ?? 0) === 1) {
      getCsrContacts(reportInputs?.programId ?? -1, Number(selectedValues));
    }
    setAllMerchantGroupsSelected(hasAllSelected);
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValues: string[] = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    const { id } = e.currentTarget;
    setReportInputs({
      ...reportInputs,
      [id]: selectedValues,
    });
  };

  const handleCsrChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.currentTarget;
    setReportInputs({
      ...reportInputs,
      [id]: value ?? null,
    });
  };

  const getMerchantGroups = (programId: number) => {
    if (!programId) return;
    ContactApi.getMerchantGroups(programId).then((res) => {
      const result = res.data;
      setMerchantGroups(result);
    });
  };

  const getReportGroups = (programId: number) => {
    if (!programId) return;
    ContactApi.getReportGroups(programId).then((res) => {
      const result = res.data;
      setReportGroups(result);
    });
  };

  const getLocations = (merchantGroupIds: string[]) => {
    ContactApi.getLocations(merchantGroupIds).then((res) => {
      const result = res.data;
      setLocations(result);
    });
  };

  const getCsrContacts = (programId: number, merchantGroupId: number) => {
    ContactApi.getCsrContacts(programId, merchantGroupId).then((res) => {
      const result = res.data;
      result.sort((a, b) =>
        a.firstName.localeCompare(b.firstName, "en", { sensitivity: "base" })
      );
      setCsrContacts(result);
    });
  };

  const getCampaigns = (programId: number) => {
    ContactApi.getCampaigns(programId).then((res) => {
      const result = res.data;
      setCampaigns(result);
    });
  };

  const hasRequiredData = () => {
    return (
      reportInputs?.fromDate && reportInputs?.toDate && reportInputs?.programId
    );
  };

  const isValid = hasRequiredData();

  return (
    <div className="report-inputs">
      <div className="row d-flex justify-content-center">
        <div className="col-4">
          <DatePicker
            className="form-control form-control-sm"
            placeholderText=" From Date"
            showIcon
            id="fromDate"
            toggleCalendarOnIconClick
            selected={reportInputs?.fromDate ?? ""}
            onChange={(date: Date | null) => handleDateChange("fromDate", date)}
            maxDate={reportInputs?.toDate ?? new Date()} 
            showMonthDropdown           
            showYearDropdown
            yearDropdownItemNumber={15} 
            onMonthChange={(date: Date | null) => handleDateChange("fromDate", date)}  
            onYearChange={(date: Date | null) => handleDateChange("fromDate", date)}                      
          />
        </div>
        <div className="col-4">
          <DatePicker
            className="form-control form-control-sm"
            placeholderText=" To Date"
            showIcon
            id="toDate"
            toggleCalendarOnIconClick
            selected={reportInputs?.toDate ?? ""}
            onChange={(date: Date | null) => handleDateChange("toDate", date)}
            minDate={reportInputs?.fromDate ?? ""}
            showMonthDropdown
            showYearDropdown  
            yearDropdownItemNumber={15}  
            onMonthChange={(date: Date | null) => handleDateChange("toDate", date)}  
            onYearChange={(date: Date | null) => handleDateChange("toDate", date)} 
          />
        </div>
      </div>
      <div className="row mt-3 mb-4">
        <div className="col-6">
          <label htmlFor="programId" className="form-label">
            Program:
          </label>
          <select
            className="dropdown form-control form-control-sm"
            value={reportInputs?.programId || ""}
            id="programId"
            name="programId"
            onChange={handleProgramChange}
          >
            <option value={""}>--Select--</option>
            {programs?.map((p: Program) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-6">
          <label htmlFor="resultInLocalTime" className="form-label">
            Time Zone Type:
          </label>
          <select
            className="dropdown form-control form-control-sm"
            value={reportInputs.resultInLocalTime || 0}
            id="resultInLocalTime"
            name="resultInLocalTime"
            onChange={handleSelectChange}
          >
            {TimeZoneTypes.map((tz: TimeZoneType) => (
              <option key={tz.id} value={tz.id}>
                {tz.description}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* the program is associated with report groups so handle layout */}
      {(reportGroups?.length ?? 0) > 0 && (
        <div className="row mt-3 mb-4">
          {allMerchantGroupsSelected && (
            <>
              <div className="col-6">
                <label htmlFor="reportGroupId" className="form-label">
                  Report Group:
                </label>
                <select
                  className="dropdown form-control form-control-sm"
                  disabled={!reportInputs?.programId}
                  value={
                    reportInputs?.reportGroupId != null
                      ? reportInputs?.reportGroupId
                      : ""
                  }
                  id="reportGroupId"
                  name="reportGroupId"
                  onChange={handleSelectChange}
                >
                  <option value={""}>National</option>
                  {reportGroups.map((rg: ReportGroup) => (
                    <option key={rg.id} value={rg.id}>
                      {rg.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6">
                <label htmlFor="merchantGroups" className="form-label">
                  Merchant Group:
                </label>
                <select
                  multiple={true}
                  className="dropdown form-control form-control-sm"
                  disabled={!reportInputs?.programId}
                  value={
                    (reportInputs?.merchantGroups?.length ?? 0) > 0
                      ? reportInputs?.merchantGroups
                      : [""]
                  }
                  id="merchantGroups"
                  name="merchantGroups"
                  onChange={handleMerchantGroupChange}
                >
                  <option value={""}>All</option>
                  {merchantGroups.map((mg: MerchantGroup) => (
                    <option key={mg.id} value={mg.id}>
                      {mg.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          {!allMerchantGroupsSelected && (
            <>
              <div className="col-6">
                <label htmlFor="merchantGroups" className="form-label">
                  Merchant Group:
                </label>
                <select
                  multiple={true}
                  className="dropdown form-control form-control-sm"
                  disabled={!reportInputs?.programId}
                  value={
                    (reportInputs?.merchantGroups?.length ?? 0) > 0
                      ? reportInputs?.merchantGroups
                      : [""]
                  }
                  id="merchantGroups"
                  name="merchantGroups"
                  onChange={handleMerchantGroupChange}
                >
                  <option value={""}>All</option>
                  {merchantGroups.map((mg: MerchantGroup) => (
                    <option key={mg.id} value={mg.id}>
                      {mg.name}
                    </option>
                  ))}
                </select>
              </div>
              {(reportInputs?.merchantGroups?.length ?? 0) === 1 && usesLocations &&(
                <>
                  <div className="col-6">
                    <label htmlFor="locations" className="form-label">
                      Locations:
                    </label>
                    <select
                      multiple={true}
                      className="dropdown form-control form-control-sm"
                      value={
                        (reportInputs?.locations?.length ?? 0) > 0
                          ? reportInputs?.locations
                          : [""]
                      }
                      id="locations"
                      name="locations"
                      onChange={handleLocationChange}
                    >
                      <option value={""}>All</option>
                      {locations.map((l: MerchantGroupLocation) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="row d-flex justify-content-center">
                    <div className="col-4 mt-3">
                      <select
                        className="dropdown form-control form-control-sm"
                        value={reportInputs?.csr || ""}
                        id="csr"
                        name="csr"
                        onChange={handleCsrChange}
                      >
                        <option value={""}>Select User</option>
                        {csrContacts.map((c: CsrContact) => (
                          <option key={c.id} value={c.id}>
                            {c.firstName} {c.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
      {(reportGroups?.length ?? 0) === 0 && (
        <div className="row mt-3 mb-4">
          <div className="col-6">
            <label htmlFor="merchantGroups" className="form-label">
              Merchant Group:
            </label>
            <select
              multiple={true}
              className="dropdown form-control form-control-sm"
              disabled={!reportInputs?.programId}
              value={
                (reportInputs?.merchantGroups?.length ?? 0) > 0
                  ? reportInputs?.merchantGroups
                  : [""]
              }
              id="merchantGroups"
              name="merchantGroups"
              onChange={handleMerchantGroupChange}
            >
              <option value={""}>All</option>
              {merchantGroups.map((mg: MerchantGroup) => (
                <option key={mg.id} value={mg.id}>
                  {mg.name}
                </option>
              ))}
            </select>
          </div>
          {!allMerchantGroupsSelected &&
            usesLocations &&
            reportInputs?.programId &&
            (reportInputs?.merchantGroups?.length ?? 0) === 1 && (
              <>
                <div className="col-6">
                  <label htmlFor="locations" className="form-label">
                    Locations:
                  </label>
                  <select
                    multiple={true}
                    className="dropdown form-control form-control-sm"
                    value={
                      (reportInputs?.locations?.length ?? 0) > 0
                        ? reportInputs?.locations
                        : [""]
                    }
                    id="locations"
                    name="locations"
                    onChange={handleLocationChange}
                  >
                    <option value={""}>All</option>
                    {locations.map((l: MerchantGroupLocation) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </div>
                {usesUsers && (
                  <div className="row d-flex justify-content-center">
                    <div className="col-4 mt-3">
                      <select
                        className="dropdown form-control form-control-sm"
                        value={reportInputs?.csr || ""}
                        id="csr"
                        name="csr"
                        onChange={handleCsrChange}
                      >
                        <option value={""}>Select User</option>
                        {csrContacts.map((c: CsrContact) => (
                          <option key={c.id} value={c.id}>
                            {c.firstName} {c.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
        </div>
      )}
      {campaigns && campaigns.length > 0 && (
        <div className="row d-flex justify-content-center">
          <div className="col-4 mt-3">
            <select
              className="dropdown form-control form-control-sm"
              value={reportInputs?.campaignId || ""}
              id="campaignId"
              name="campaignId"
              onChange={handleSelectChange}
            >
              <option value={""}>All Campaigns</option>
              {campaigns.map((c: Campaign) => (
                <option key={c.id} value={c.id}>
                  {c.description}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div className="row mt-3 mb-2">
        <div className="col-6">
          <div className="d-grid text-center">
            <input
              type="submit"
              className="btn btn-sm btn-secondary"
              value="View Report"
              disabled={!isValid}
              onClick={handleViewReport}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="d-grid text-center">
            <input
              type="submit"
              className="btn btn-sm btn-secondary"
              value="Download Report"
              disabled={!isValid}
              onClick={handleDownloadReport}
            />
          </div>
        </div>
        {exceedsMaxDateRange && (
          <div className="col-12">
            <div className="d-grid text-center validation-message">
              {`Please reduce your date range to no more than ${appSettings.dateRangeMonthsLimit_View} months to
                    view online or ${appSettings.dateRangeMonthsLimit_Download} months to download.`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchCriteria;
